import {
  campaignApiDelete,
  campaignApiGet,
  campaignApiPatch,
  campaignApiPost,
} from '@/lib/api/campaign-api-client';
import type {
  CampaignApiEnvelope,
  TemplateApi,
  TemplateContentVersionDetailApi,
  TemplateContentVersionSummaryApi,
  TemplateDeleteResultApi,
  TemplatesPageApi,
} from '../types/templates-api.types';
import type {
  CreateTemplateInput,
  GetTemplatesParams,
  Template,
  TemplateContentVersionDetail,
  TemplateContentVersionSummary,
  TemplatesPagingResult,
  UpdateTemplateInput,
} from '../types/templates.types';
import {
  mapTemplateApi,
  mapTemplateContentVersionDetailApi,
  mapTemplateContentVersionSummaryApi,
  mapTemplateListItemApi,
  toTemplatesPagingResult,
} from '../utils/template-api-mapper';

const assertTemplateApiSuccess = <T>(
  response: CampaignApiEnvelope<T> | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  if (response.data === undefined || response.data === null) {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return response.data;
};

const requireTemplateId = (templateId: string): string => {
  const id = templateId.trim();
  if (!id) {
    throw new Error('Template id is required');
  }
  return id;
};

export const getTemplates = async (
  params: GetTemplatesParams = {}
): Promise<TemplatesPagingResult> => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 50;
  const offset = Math.max(0, (pageNo - 1) * pageSize);
  const q = params.q?.trim() || undefined;
  const sort = params.sort ?? 'created';
  const order = params.order ?? 'desc';

  const response = await campaignApiGet<CampaignApiEnvelope<TemplatesPageApi>>('/v1/templates', {
    limit: pageSize,
    offset,
    sort,
    order,
    q,
    category: params.category,
  });
  const data = assertTemplateApiSuccess(response, 'Failed to fetch templates');

  return toTemplatesPagingResult(
    data.items.map(mapTemplateListItemApi),
    pageNo,
    pageSize,
    data.pageInfo
  );
};

export const getTemplateById = async (templateId: string): Promise<Template> => {
  const id = requireTemplateId(templateId);
  const response = await campaignApiGet<CampaignApiEnvelope<TemplateApi>>(
    `/v1/templates/${encodeURIComponent(id)}`
  );
  const data = assertTemplateApiSuccess(response, 'Failed to fetch template');
  return mapTemplateApi(data);
};

export const insertTemplate = async (input: CreateTemplateInput): Promise<Template> => {
  const response = await campaignApiPost<CampaignApiEnvelope<TemplateApi>>('/v1/templates', {
    name: input.name,
    html: input.html,
    ...(input.description ? { description: input.description } : {}),
    ...(input.category ? { category: input.category } : {}),
  });
  const data = assertTemplateApiSuccess(response, 'Failed to create template');
  return mapTemplateApi(data);
};

export const updateTemplate = async (
  templateId: string,
  input: UpdateTemplateInput
): Promise<Template> => {
  const id = requireTemplateId(templateId);
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.html !== undefined) body.html = input.html;
  if (input.description !== undefined) body.description = input.description;
  // On PATCH `null` means "leave unchanged"; clearing to uncategorized is an empty string.
  if (input.category !== undefined) body.category = input.category ?? '';

  const response = await campaignApiPatch<CampaignApiEnvelope<TemplateApi>>(
    `/v1/templates/${encodeURIComponent(id)}`,
    body
  );
  const data = assertTemplateApiSuccess(response, 'Failed to update template');
  return mapTemplateApi(data);
};

export const deleteTemplate = async (templateId: string): Promise<TemplateDeleteResultApi> => {
  const id = requireTemplateId(templateId);
  const response = await campaignApiDelete<CampaignApiEnvelope<TemplateDeleteResultApi>>(
    `/v1/templates/${encodeURIComponent(id)}`
  );
  return assertTemplateApiSuccess(response, 'Failed to delete template');
};

export const duplicateTemplate = async (templateId: string): Promise<Template> => {
  const id = requireTemplateId(templateId);
  const response = await campaignApiPost<CampaignApiEnvelope<TemplateApi>>(
    `/v1/templates/${encodeURIComponent(id)}/duplicate`,
    {}
  );
  const data = assertTemplateApiSuccess(response, 'Failed to duplicate template');
  return mapTemplateApi(data);
};

export const getTemplateContentVersions = async (
  templateId: string
): Promise<TemplateContentVersionSummary[]> => {
  const id = requireTemplateId(templateId);
  const response = await campaignApiGet<CampaignApiEnvelope<TemplateContentVersionSummaryApi[]>>(
    `/v1/templates/${encodeURIComponent(id)}/content/versions`
  );
  const data = assertTemplateApiSuccess(response, 'Failed to fetch template versions');
  return data.map(mapTemplateContentVersionSummaryApi);
};

export const getTemplateContentVersion = async (
  templateId: string,
  version: number
): Promise<TemplateContentVersionDetail> => {
  const id = requireTemplateId(templateId);
  const response = await campaignApiGet<CampaignApiEnvelope<TemplateContentVersionDetailApi>>(
    `/v1/templates/${encodeURIComponent(id)}/content/versions/${version}`
  );
  const data = assertTemplateApiSuccess(response, 'Failed to fetch template version');
  return mapTemplateContentVersionDetailApi(data);
};
